// ============================================================================
// automationBus — fire-and-forget dispatch surface for v1 defs + v2 graphs.
// ----------------------------------------------------------------------------
// A single AutomationDispatchProvider (mounted near App root) subscribes to the
// active workspace's automations and keeps the latest snapshot here. Emit sites
// throughout the app call `fireAutomationEvent(event, actor)` without needing
// to know which workspace is active or what's subscribed. Best-effort only;
// failures are swallowed to protect the caller's flow.
// ============================================================================

import { dispatchAutomationEventV2 } from './automationOrchestrator'
import { isAuthoritativeAutomationEvent, publishAutomationEventToWorkspaceActivity } from './automationEventIngestion'
import type { AutomationActor } from './automationService'
import type { AutomationDefinition, AutomationGraph, AutomationTriggerEvent } from '../types/automation'

type BusSnapshot = {
  workspaceId: string | null
  definitions: AutomationDefinition[]
  graphs: AutomationGraph[]
}

const snapshot: BusSnapshot = { workspaceId: null, definitions: [], graphs: [] }

const BACKEND_SAFE_ACTION_TYPES = new Set(['CREATE_NODE', 'CREATE_CHECKLIST_ITEM', 'NOTIFY_USER', 'UPDATE_FIELD', 'WEBHOOK', 'COMPLETE_NODE', 'ASSIGN_TASK', 'SEND_CHAT_MESSAGE', 'LOG_TO_LEDGER'])

const hasActiveMatchForTrigger = (
  definitions: AutomationDefinition[],
  graphs: AutomationGraph[],
  event: AutomationTriggerEvent,
): boolean =>
  definitions.some((automation) => automation.status === 'ACTIVE' && automation.trigger.type === event.type)
  || graphs.some((graph) => graph.status === 'ACTIVE' && graph.triggers.some((trigger) => trigger.type === event.type))

const matchingWorkflowsRequireLocalRuntime = (
  definitions: AutomationDefinition[],
  graphs: AutomationGraph[],
  event: AutomationTriggerEvent,
): boolean =>
  definitions.some((automation) =>
    automation.status === 'ACTIVE'
    && automation.trigger.type === event.type
    && automation.actions.some((action) => !BACKEND_SAFE_ACTION_TYPES.has(action.type)),
  ) || graphs.some((graph) =>
    graph.status === 'ACTIVE'
    && graph.triggers.some((trigger) => trigger.type === event.type)
    && [...graph.then, ...(graph.else ?? [])].some((action) => !BACKEND_SAFE_ACTION_TYPES.has(action.type)),
  )

export const setAutomationBusSnapshot = (
  workspaceId: string | null,
  definitions: AutomationDefinition[],
  graphs: AutomationGraph[],
): void => {
  snapshot.workspaceId = workspaceId
  snapshot.definitions = definitions
  snapshot.graphs = graphs
}

export const fireAutomationEvent = (
  event: AutomationTriggerEvent,
  actor: AutomationActor & { role?: string; walletCredits?: number; planTier?: string },
): void => {
  if (!snapshot.workspaceId || snapshot.workspaceId !== event.workspaceId) return
  if (snapshot.definitions.length === 0 && snapshot.graphs.length === 0) return

  if (!hasActiveMatchForTrigger(snapshot.definitions, snapshot.graphs, event)) return

  const dispatchLocal = () => void dispatchAutomationEventV2(snapshot.definitions, snapshot.graphs, event, actor).catch((error) => {
    console.warn('[automationBus] dispatch failed', { type: event.type }, error)
  })

  if (!isAuthoritativeAutomationEvent(event) || matchingWorkflowsRequireLocalRuntime(snapshot.definitions, snapshot.graphs, event)) {
    dispatchLocal()
    return
  }

  void publishAutomationEventToWorkspaceActivity(event, actor).catch((error) => {
    console.warn('[automationBus] authoritative publish failed, falling back to local runtime', { type: event.type }, error)
    dispatchLocal()
  })
}
